import * as Sentry from '@sentry/react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
// Placeholder component
import PlaceholderPage from './components/PlaceholderPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import AdminLayout from './layouts/AdminLayout';
import { detectAppContext } from './lib/context';
import LoginPage from './pages/LoginPage';
import SystemAdminList from './pages/platform/admins/SystemAdminList';
// Platform (Vendor/System)
import DepartmentClassificationList from './pages/platform/master/DepartmentClassificationList';
import InstitutionDetail from './pages/platform/tenants/InstitutionDetail';
import InstitutionList from './pages/platform/tenants/InstitutionList';
import TenantCreationWizard from './pages/platform/tenants/TenantCreationWizard';
import TenantDetail from './pages/platform/tenants/TenantDetail';
import TenantList from './pages/platform/tenants/TenantList';
import UnsetupInstitutionsPage from './pages/platform/tenants/UnsetupInstitutionsPage';
import VendorList from './pages/platform/vendors/VendorList';

import ApplicationDetail from './pages/tenant/applications/ApplicationDetail';
// Tenant - Applications
import ApplicationList from './pages/tenant/applications/ApplicationList';
import ApplicationStatusForm from './pages/tenant/applications/ApplicationStatusForm';
import ApplicationUpload from './pages/tenant/applications/ApplicationUpload';
import ApprovalQueue from './pages/tenant/applications/ApprovalQueue';
import AnnouncementForm from './pages/tenant/content/AnnouncementForm';
// Tenant - Content
import AnnouncementList from './pages/tenant/content/AnnouncementList';
import DocumentForm from './pages/tenant/content/DocumentForm';
import DocumentList from './pages/tenant/content/DocumentList';
import EmailTemplateForm from './pages/tenant/content/EmailTemplateForm';
import EmailTemplateList from './pages/tenant/content/EmailTemplateList';
import { StaticPageForm } from './pages/tenant/content/StaticPageForm';
import { StaticPageList } from './pages/tenant/content/StaticPageList';
import FeeForm from './pages/tenant/enrollment/FeeForm';
// Tenant - Enrollment
import FeeList from './pages/tenant/enrollment/FeeList';
import StudentForm from './pages/tenant/enrollment/StudentForm';
import StudentList from './pages/tenant/enrollment/StudentList';
import BillingItemList from './pages/tenant/master/BillingItemList';
import CatalogDetail from './pages/tenant/master/CatalogDetail';
import CatalogList from './pages/tenant/master/CatalogList';
import ChangeRequestForm from './pages/tenant/master/ChangeRequestForm';
import ChangeRequestList from './pages/tenant/master/ChangeRequestList';
import ExamForm from './pages/tenant/master/ExamForm';
import ExamList from './pages/tenant/master/ExamList';
import ExamOfferingForm from './pages/tenant/master/ExamOfferingForm';
import ExamOfferingList from './pages/tenant/master/ExamOfferingList';
import NotificationForm from './pages/tenant/master/NotificationForm';
import NotificationList from './pages/tenant/master/NotificationList';
import OrganizationDetail from './pages/tenant/master/OrganizationDetail';
// Tenant (University) - Master
import OrganizationList from './pages/tenant/master/OrganizationList';
import VenueForm from './pages/tenant/master/VenueForm';
import VenueList from './pages/tenant/master/VenueList';
// Tenant - Messages
import MessageList from './pages/tenant/messages/MessageList';
import { PaymentList } from './pages/tenant/payment/PaymentList';

// Tenant - Settings (User Config)
import BasicSettings from './pages/tenant/settings/BasicSettings';
import BrandingSettings from './pages/tenant/settings/BrandingSettings';
import ProfileSettings from './pages/tenant/settings/ProfileSettings';
import TenantDashboard from './pages/tenant/TenantDashboard';
import UserList from './pages/tenant/users/UserList';

function App() {
  const context = detectAppContext();

  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <AuthProvider>
        <TenantProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Routes - No authentication required */}
              <Route path="/login" element={<LoginPage mode={context === 'platform' ? 'vendor' : 'tenant'} />} />
              <Route path="/platform/login" element={<LoginPage mode="vendor" />} />

              {/* Protected Routes - Wrapped with ProtectedRoute + AdminLayout */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                {/* Index: Redirect based on context */}
                <Route
                  index
                  element={context === 'platform' ? <Navigate to="system/tenants" replace /> : <TenantDashboard />}
                />

                <Route path="dashboard" element={<TenantDashboard />} />

                {/*  ===== システム管理 (System Management) =====  */}
                <Route path="system/tenants" element={<TenantList />} />
                <Route path="system/tenants/wizard" element={<TenantCreationWizard />} />
                <Route path="system/tenants/create" element={<TenantCreationWizard />} />
                <Route path="system/tenants/:id" element={<TenantDetail />} />
                <Route path="system/tenants/:tenantId/unsetup-institutions" element={<UnsetupInstitutionsPage />} />
                <Route path="system/institutions" element={<InstitutionList />} />
                <Route path="system/institutions/:id" element={<InstitutionDetail />} />
                <Route path="system/admins" element={<SystemAdminList />} />
                <Route path="system/vendors" element={<VendorList />} />
                <Route path="system/master/classifications" element={<DepartmentClassificationList />} />

                {/* Platform routes (legacy redirect) */}
                <Route path="platform" element={<Navigate to="/system/tenants" replace />} />
                <Route path="platform/tenants" element={<Navigate to="/system/tenants" replace />} />
                <Route path="platform/tenants/*" element={<Navigate to="/system/tenants" replace />} />
                <Route path="platform/institutions" element={<Navigate to="/system/institutions" replace />} />
                <Route path="platform/admins" element={<Navigate to="/system/admins" replace />} />
                <Route path="platform/vendors" element={<Navigate to="/system/vendors" replace />} />

                {/* ===== サイト構築 (Site Setup) - US-4系 ===== */}
                <Route path="site/organizations" element={<OrganizationList />} />
                <Route path="site/organizations/new" element={<OrganizationDetail />} />
                <Route path="site/organizations/:id" element={<OrganizationDetail />} />
                <Route path="site/organizations/:id/change" element={<ChangeRequestForm />} />
                <Route path="site/change-requests" element={<ChangeRequestList />} />
                <Route path="site/notifications" element={<NotificationList />} />
                <Route path="site/notifications/new" element={<NotificationForm />} />
                <Route path="site/catalogs" element={<CatalogList />} />
                <Route path="site/catalogs/:id" element={<CatalogDetail />} />
                <Route path="site/billing-items" element={<BillingItemList />} />
                <Route path="site/exams" element={<ExamList />} />
                <Route path="site/exams/new" element={<ExamForm />} />
                <Route path="site/exams/:id" element={<ExamForm />} />
                <Route path="site/offerings" element={<ExamOfferingList />} />
                <Route path="site/offerings/new" element={<ExamOfferingForm />} />
                <Route path="site/offerings/:id" element={<ExamOfferingForm />} />
                <Route path="site/venues" element={<VenueList />} />
                <Route path="site/venues/:id" element={<VenueForm />} />
                <Route
                  path="site/labels"
                  element={<PlaceholderPage titleKey="pages.labels.title" descriptionKey="pages.labels.description" />}
                />

                {/* ===== 出願期対応 (Application Phase) - US-1系 ===== */}
                <Route path="applications/search" element={<ApplicationList />} />
                <Route path="applications/:id" element={<ApplicationDetail />} />
                <Route
                  path="applications/accounts"
                  element={
                    <PlaceholderPage
                      titleKey="pages.accountSearch.title"
                      descriptionKey="pages.accountSearch.description"
                    />
                  }
                />
                <Route path="applications/status" element={<ApplicationStatusForm />} />
                <Route path="applications/upload" element={<ApplicationUpload />} />
                <Route path="applications/approvals" element={<ApprovalQueue />} />

                {/* ===== 選考・結果発表 (Selection Phase) ===== */}
                <Route
                  path="selection/results"
                  element={
                    <PlaceholderPage
                      titleKey="pages.selectionResults.title"
                      descriptionKey="pages.selectionResults.description"
                    />
                  }
                />
                <Route
                  path="selection/announcement"
                  element={
                    <PlaceholderPage
                      titleKey="pages.resultAnnouncement.title"
                      descriptionKey="pages.resultAnnouncement.description"
                    />
                  }
                />

                {/* ===== 入学手続き (Enrollment Phase) - US-3系 ===== */}
                <Route path="enrollment/students" element={<StudentList />} />
                <Route path="enrollment/students/new" element={<StudentForm />} />
                <Route path="enrollment/students/:id" element={<StudentForm />} />
                <Route path="enrollment/fees" element={<FeeList />} />
                <Route path="enrollment/fees/new" element={<FeeForm />} />
                <Route path="enrollment/fees/:id" element={<FeeForm />} />
                <Route
                  path="enrollment/upload"
                  element={
                    <PlaceholderPage
                      titleKey="pages.enrollUpload.title"
                      descriptionKey="pages.enrollUpload.description"
                    />
                  }
                />
                <Route path="enrollment/payments" element={<PaymentList />} />

                {/* ===== コミュニケーション (Communication) - US-2系 ===== */}
                <Route path="comms/announcements" element={<AnnouncementList />} />
                <Route path="comms/announcements/new" element={<AnnouncementForm />} />
                <Route path="comms/announcements/:id" element={<AnnouncementForm />} />
                <Route path="comms/messages" element={<MessageList />} />
                <Route
                  path="comms/messages/:id"
                  element={
                    <PlaceholderPage
                      titleKey="pages.messageDetail.title"
                      descriptionKey="pages.messageDetail.description"
                    />
                  }
                />
                <Route
                  path="comms/messages/new"
                  element={
                    <PlaceholderPage titleKey="pages.messageNew.title" descriptionKey="pages.messageNew.description" />
                  }
                />
                <Route path="comms/emails" element={<EmailTemplateList />} />
                <Route path="comms/emails/new" element={<EmailTemplateForm />} />
                <Route path="comms/emails/:id" element={<EmailTemplateForm />} />

                {/* ===== 帳票・出力 (Reports) - US-5系 ===== */}
                <Route
                  path="reports/batch"
                  element={
                    <PlaceholderPage
                      titleKey="pages.reportsBatch.title"
                      descriptionKey="pages.reportsBatch.description"
                    />
                  }
                />
                <Route
                  path="reports/application"
                  element={
                    <PlaceholderPage
                      titleKey="pages.reportsApplication.title"
                      descriptionKey="pages.reportsApplication.description"
                    />
                  }
                />
                <Route
                  path="reports/enrollment"
                  element={
                    <PlaceholderPage
                      titleKey="pages.reportsEnrollment.title"
                      descriptionKey="pages.reportsEnrollment.description"
                    />
                  }
                />
                <Route
                  path="reports/files"
                  element={
                    <PlaceholderPage
                      titleKey="pages.reportsFiles.title"
                      descriptionKey="pages.reportsFiles.description"
                    />
                  }
                />

                {/* ===== 関連資料 (Content Assets) - US-403 ===== */}
                <Route path="content/documents" element={<DocumentList />} />
                <Route path="content/documents/new" element={<DocumentForm />} />
                <Route path="content/static" element={<StaticPageList />} />
                <Route path="content/static/:code" element={<StaticPageForm />} />

                {/* ===== 設定 (Settings) - US-6系 ===== */}
                <Route path="settings/users" element={<UserList />} />
                <Route
                  path="settings/permissions"
                  element={
                    <PlaceholderPage
                      titleKey="pages.permissions.title"
                      descriptionKey="pages.permissions.description"
                    />
                  }
                />
                <Route path="settings/basic" element={<BasicSettings />} />
                <Route path="settings/branding" element={<BrandingSettings />} />
                <Route path="settings/profile" element={<ProfileSettings />} />

                {/* ===== 監査・ログ (Audit) - US-602 ===== */}
                <Route
                  path="audit/logs"
                  element={
                    <PlaceholderPage titleKey="pages.auditLogs.title" descriptionKey="pages.auditLogs.description" />
                  }
                />

                {/* ===== リリース (Release) - US-404 ===== */}
                <Route
                  path="release/preview"
                  element={
                    <PlaceholderPage
                      titleKey="pages.releasePreview.title"
                      descriptionKey="pages.releasePreview.description"
                    />
                  }
                />
                <Route
                  path="release/deploy"
                  element={
                    <PlaceholderPage
                      titleKey="pages.releaseDeploy.title"
                      descriptionKey="pages.releaseDeploy.description"
                    />
                  }
                />

                {/* Fallback */}
                <Route
                  path="*"
                  element={
                    <PlaceholderPage titleKey="pages.notFound.title" descriptionKey="pages.notFound.description" />
                  }
                />
              </Route>
            </Routes>
          </BrowserRouter>
        </TenantProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
